import { ApolloError, gql } from 'apollo-boost';
import c from 'classnames';
import { useDeviceInfo } from 'core/components/DeviceInfoContext';
import InlineEdit from 'core/components/InlineEdit';
import { showSnackbar } from 'core/components/snackbar';
import useToggle from 'core/hooks/useToggle';
import { button } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { numberSeparateThousands } from 'core/utils';
import { getErrors } from 'core/utils/apollo';
import { formatToLocalDayMonthYearDot } from 'core/utils/date';
import React from 'react';
import { useUpdate52weeksMutation } from 'site/graphql';
import { validatePositiveNumber } from '../financial-calculators/const';
import { IWeekInvestment, sliceWeekInvestments } from './helpers';
import Service52WeeksMobileTable from './Service52WeeksMobileTable';

gql`
    mutation Update52weeks($weekNumber: Int!, $sum: Decimal!) {
        update52weeks(weekNumber: $weekNumber, sum: $sum) {
            id
            startDate
            startSum
            investments {
                id
                sum
                weekNumber
            }
        }
    }
`;

type IService52WeeksTableProps = {
    weekInvestments: IWeekInvestment[];
} & WithStyles<typeof styles>;

const Service52WeeksTable: React.FC<IService52WeeksTableProps> = ({ classes, weekInvestments }) => {
    const [updateWeek] = useUpdate52weeksMutation({
        onError: (error: ApolloError) => showSnackbar({ snackbarErrors: getErrors(error) }),
    });
    const { isMobile } = useDeviceInfo();
    const [toggle, { switchToggle }] = useToggle();

    const handleChangeWeek = (val: string, week: IWeekInvestment) => {
        updateWeek({
            variables: { sum: parseInt(val, 10), weekNumber: week.weekNumber },
        });

        if (week.isCurrentWeek && !week.fact.investment) {
            showSnackbar({ snackbarMessage: 'Поздравляем, ты на шаг ближе к цели!' });
        }
    };

    if (isMobile) {
        return <Service52WeeksMobileTable weekInvestments={weekInvestments} onChangeWeek={handleChangeWeek} />;
    }

    const tableData = toggle ? weekInvestments : sliceWeekInvestments(weekInvestments, 10);

    return (
        <>
            <table className={classes.table}>
                <thead>
                    <tr>
                        <th></th>
                        <th colSpan={2}>План</th>
                        <th colSpan={2}>Факт</th>
                    </tr>
                    <tr>
                        <th>Неделя</th>
                        <th>Отложить</th>
                        <th>Сумма</th>
                        <th>Отложено</th>
                        <th>Сумма</th>
                    </tr>
                </thead>
                <tbody>
                    {tableData.map(w => (
                        <tr key={w.weekNumber} className={c({ [classes.currentWeek]: w.isCurrentWeek })}>
                            <td>
                                {formatToLocalDayMonthYearDot(w.from)} - {formatToLocalDayMonthYearDot(w.to)}
                            </td>
                            <td>{numberSeparateThousands(w.plan.investment)}</td>
                            <td>{numberSeparateThousands(w.plan.sum)}</td>
                            <td>
                                {w.fact.investment !== null ? (
                                    <InlineEdit
                                        text={w.fact.investment.toString()}
                                        formatter={numberSeparateThousands}
                                        change={val => handleChangeWeek(val, w)}
                                        validate={validatePositiveNumber}
                                        className={classes.staticInlineEdit}
                                        activeClassName={classes.editingInlineEdit}
                                    />
                                ) : (
                                    '-'
                                )}
                            </td>
                            <td>{w.fact.sum !== null ? numberSeparateThousands(w.fact.sum) : '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <button onClick={switchToggle} className={classes.showAll}>
                {toggle ? 'Скрыть' : 'Показать'} все
            </button>
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        table: {
            width: 100 + '%',
            borderCollapse: 'collapse',
            '& tr': {
                borderBottom: '1px solid #E5E5E5',
                height: 40,
            },
            '& thead th': {
                position: 'sticky',
                top: 0,
                background: '#fff',
            },
            
            '& thead tr:nth-child(2) th': {
                top: '38px'
            },
            '& thead tr:first-child': {
                borderBottom: 'none',
                color: theme.colors.accent,
            },

            '& thead tr:last-child': {
                color: '#808080',
            },

            '& > tbody > tr:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },

            '& td, th': {
                fontSize: 16,
                textAlign: 'center',
            },
        },
        currentWeek: {
            outline: `1px solid ${theme.colors.accent}`,
        },
        staticInlineEdit: {
            border: `1px dashed transparent`,
            borderBottomColor: theme.colors.accent,
            cursor: 'pointer',

            '&:hover, &:focus': {
                outline: 'none',
                borderColor: theme.colors.accent,
            },
        },
        editingInlineEdit: {
            width: 80,
            outline: 'none',
        },
        showAll: {
            ...button(theme),
            fontWeight: 'bold',
            border: `1px solid #DADADA`,
            alignSelf: 'end',
            fontSize: 0.9 + 'rem',
            margin: '0 auto',
            display: 'block',

            '&:hover, &:focus': {
                color: 'white',
                backgroundColor: '#1E252F',
            },
        },
    });

export default injectSheet(styles)(Service52WeeksTable);
